import { Injectable } from '@angular/core';
import { catchError, concatWith, delay, filter, map, switchMap, tap } from 'rxjs/operators';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AuthenticationService } from '../../core/authentication.service';
import { concatLatestFrom } from '@ngrx/operators';
import * as globalActions from './core-actions';
import { CoreLocationDataActions, CoreUserActions, ManagedLocationCountActions, noop } from './core-actions';
import { combineLatestWith, EMPTY, from, of } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { FeedbackService } from '../../core/feedback.service';
import { LoadingSpinnerActions } from '../../components/locations-skeleton/location-list/state/location-list-actions';
import { ApiService } from '../../core/api-service';
import { getFromLocalStorage, sendToGoogleAnalytics, setToLocalStorage } from '../../core/utilities';
import { TranslateService } from '@ngx-translate/core';
import { selectAccountID, selectGMClientApiKey, selectUserLanguage } from './core-selectors';
import { ScriptService } from '../../core/script.service';


@Injectable()
export class CoreEffects {
  constructor(
      private store: Store,
      private authService: AuthenticationService,
      private actions$: Actions,
      private feedbackService: FeedbackService,
      private apiService: ApiService,
      private scriptService: ScriptService,
      private translate: TranslateService) {
  }

  updateUser$ = createEffect(() => {
    return this.authService.user$.pipe(
        map((user) => {
          return CoreUserActions.updateUser({ user });
        }),
    );
  });

  updateUserLanguage$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(CoreUserActions.updateUserLanguage),
        tap((action) => {
          this.translate.use(action.languageShort);
        }),
        switchMap((action) => this.apiService.changeUserLanguage(action.languageShort).pipe(
            tap(() => setToLocalStorage('LANGUAGE', action.languageShort)),
            map(() => {
              return CoreUserActions.updateUserLanguageSuccess({ languageShort: action.languageShort });
            }),
            catchError((err: HttpErrorResponse) => {
              this.feedbackService.showAlertSnackbar({
                data: {
                  userFriendlyMessage: 'Updating language setting failed - current language setting will not be persisted after page refresh',
                  detailedInfoFromBE: err?.error?.message ? [err.error.message] : null,
                },
              }, err?.status);
              return of(noop);
            }),
        )),
    );
  });

  updateUserDetails$ = createEffect(() => {
    return this.authService.isAuthenticated$.pipe(
        filter(isAuthenticated => isAuthenticated),
        switchMap(() => this.apiService.getUserDetails()
            .pipe(
                tap((res) => {
                  if (res?.result?.language) {
                    this.translate.use(res.result.language);
                    setToLocalStorage('LANGUAGE', res.result.language);
                  }
                }),
                map((res) => {
                  return CoreUserActions.updateUserDetails({ userDetails: res?.result });
                }),
                catchError((err: HttpErrorResponse) => {
                  console.error(err);
                  return of(LoadingSpinnerActions.stopLoadingSpinner());
                }),
            ),
        ),
    );
  });

  resetColumnNotesSnackbarViewedStatus$ = createEffect(() => {
    return this.authService.isAuthenticated$.pipe(
        filter(isAuthenticated => isAuthenticated),
        tap(() => {
          // reset column notes' 'snackbar viewed' status upon a successful login
          if (getFromLocalStorage('COLUMN_NOTES_SNACKBAR')) {
            localStorage.removeItem('COLUMN_NOTES_SNACKBAR');
          }
        }),
    );
  }, { dispatch: false });

  trackUserLogin$ = createEffect(() => {
    return this.authService.isAuthenticated$.pipe(
        filter(isAuthenticated => isAuthenticated),
        tap(() => sendToGoogleAnalytics('User Management', 'login', 'User logged in')),
    );
  }, { dispatch: false });

  handleAuth0RefreshTokenErrors$ = createEffect(() => {
    return this.authService.auth0error$.pipe(
        filter(e =>
            e.message?.includes('Missing Refresh Token') || // Fixes BLS-4996
            e.message?.includes('Unknown or invalid refresh token.')), // Fixes BLS-5031/BLS-5077
        tap(() => this.authService.loginWithRedirect()),
    );
  }, { dispatch: false });

  loadManagedLocationCount$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(ManagedLocationCountActions.loadManagedLocationCount),
        concatLatestFrom(() => this.store.select(selectAccountID)),
        filter(([, accountId]) => !!accountId), // w8 for accountID
        switchMap(([, accountId]) => this.apiService.getLocationsCount(accountId.toString()).pipe(
            map(response => ManagedLocationCountActions.loadManagedLocationCountSuccess({ count: response.result || 0 })),
            catchError(error => of(ManagedLocationCountActions.loadManagedLocationCountFailure({ error }))),
        )),
    );
  });

  refreshManagedLocationCount$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(ManagedLocationCountActions.refreshManagedLocationCount),
        concatLatestFrom(() => this.store.select(selectAccountID)),
        filter(([, accountId]) => !!accountId), // w8 for accountID
        switchMap(([, accountId]) => this.apiService.getLocationsCount(accountId.toString()).pipe(
            map(response => ManagedLocationCountActions.refreshManagedLocationCountSuccess({ count: response.result || 0 })),
            catchError(error => of(ManagedLocationCountActions.refreshManagedLocationCountFailure({ error }))),
        )),
    );
  });

  getLocationData$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(CoreLocationDataActions.getLocationData),
        concatLatestFrom(() => [this.store.select(selectAccountID)]),
        switchMap(([action, accountId]) =>
            this.apiService.getLocationData(accountId).pipe(
                map((res) => {
                  return CoreLocationDataActions.getLocationDataSuccess({ locationData: res?.result });
                }),
                catchError((err: HttpErrorResponse) =>
                    of(CoreLocationDataActions.getLocationDataError({
                      error: err,
                      userFriendlyMessage: 'Failed to load location data.',
                    })),
                ),
            ),
        ),
    );
  });

  handleGetLocatioDataError$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(CoreLocationDataActions.getLocationDataError),
        tap((action) => {
          const message: string = action.error?.error?.message ?
              action.error?.error?.message :
              action.error?.message;

          this.feedbackService.showAlertSnackbar({
            data: {
              userFriendlyMessage: action.userFriendlyMessage,
              detailedInfoFromBE: message?.split(';'),
            },
          }, action.error?.status);
          console.error(action.error);
        }),
    );
  }, { dispatch: false });

  handleLocationDataLoading$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(CoreLocationDataActions.getLocationData,
            CoreLocationDataActions.getLocationDataSuccess,
            CoreLocationDataActions.getLocationDataError),
        map((action) => {
          return action.type === CoreLocationDataActions.getLocationData.type ?
              CoreLocationDataActions.locationDataLoading({ loading: true }) :
              CoreLocationDataActions.locationDataLoading({ loading: false });
        }),
    );
  });

  // also loads location data on app init
  reloadLocationDataOnAccountSwitch$ = createEffect(() => {
    return this.store.select(selectAccountID).pipe(
        filter((id) => !!id),
        map(() => CoreLocationDataActions.getLocationData()),
    );
  });

  // load map scripts for (mostly) internal map components
  // some (mostly client-facing) components handle this themselves (and don't reload on lang change)
  // and should be refactored to take use of this
  loadMapScripts = createEffect(() => {
    return this.store.select(selectGMClientApiKey).pipe(
        combineLatestWith(this.store.select(selectUserLanguage)),
        filter(([mapsKey]) => !!mapsKey),
        // switchMap cancels any previous pending load cycles if input changes again
        switchMap(([mapsKey]) => {
          // 1. Return the "False" action immediately as the first observable
          const resetScript$ = of(globalActions.updateGoogleMapsApiLoaded({
            googleMapsApiLoaded: false,
          }));

          // 2. Create the loading observable that starts with a delay
          const loadScript$ = of(null).pipe(
              delay(250), // Give the UI time to destroy the component
              switchMap(() =>
                  from(Promise.all([
                    this.scriptService.loadGoogleMapsScript(mapsKey, this.translate.currentLang),
                    this.scriptService.loadMarkerClustererScript(),
                  ])).pipe(
                      map(() => globalActions.updateGoogleMapsApiLoaded({
                        googleMapsApiLoaded: true,
                      })),
                      catchError((err) => {
                        console.error(err);
                        return EMPTY;
                      }),
                  ),
              ),
          );

          // Return first the reset scrip action, then the delayed load script action
          return resetScript$.pipe(concatWith(loadScript$));
        }),
    );
  });

}
